import {
  useInfiniteQuery,
  UseQueryOptions,
  useMutation,
  useQueryClient,
  useQuery,
} from "@tanstack/react-query";
import apiClient from "../apiClient";
import { toast } from "sonner";
import { paymentKeys, customerRepresentativeKeys } from "./queryKeys";
import { receivablesKeys } from "./receivablesQueries";
import type {
  EmailDraft,
  EmailThread,
  Payment,
  PaymentHistory,
} from "../../utils/interfaces";
import type { CustomerNoteHistory } from "./customerOrganizationQueries";

import type { IntentionStatus } from "../../utils/types";
import { AxiosResponse } from "axios";
import { INVOICE_PDF_ACCOUNTING_RECONNECT_MESSAGE } from "../../utils/invoicePdfErrors";
type PaginatedResponse<T> = {
  items: T[];
  total: number;
  skip: number;
  limit: number;
};

export type InvoiceFollowUp = {
  id: number;
  payment_id: number;
  follow_up_date: string;
  description?: string | null;
  created_at: string;
  created_by_id?: number | null;
};

export const useAllOpenPaymentsQuery = (
  search?: string,
  intentionStatuses?: IntentionStatus[],
) =>
  useInfiniteQuery({
    queryKey: paymentKeys.open(search, intentionStatuses),
    queryFn: async ({ pageParam = 0 }) => {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const intentionStatusParams =
        intentionStatuses && intentionStatuses.length > 0
          ? intentionStatuses
              .map(
                (status) => `&intention_status=${encodeURIComponent(status)}`,
              )
              .join("")
          : "";
      const { data } = await apiClient.get<PaginatedResponse<Payment>>(
        `/payments/?skip=${pageParam}&limit=50${searchParam}${intentionStatusParams}`,
      );
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (sum, page) => sum + page.items.length,
        0,
      );
      return totalLoaded < lastPage.total ? totalLoaded : undefined;
    },
    initialPageParam: 0,
  });

export const useAllOpenPaymentsQueryAll = () =>
  useQuery({
    queryKey: [...paymentKeys.open(), "all"],
    queryFn: async () => {
      const allPayments: Payment[] = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const { data } = await apiClient.get<PaginatedResponse<Payment>>(
          `/payments/?skip=${skip}&limit=${limit}`,
        );
        allPayments.push(...data.items);
        skip += data.items.length;
        hasMore = data.items.length === limit && skip < data.total;
      }

      return allPayments;
    },
  });

export type ArchivePayment = Payment & {
  date_created_in_accounting_software: string;
  customer_organization_name?: string;
};

export type PaidArchiveDateField = "paid_or_closed" | "invoice" | "due";
export type PaidArchiveStatus = "Paid" | "Closed";

export type PaidArchiveDashboardParams = {
  customerId?: number;
  statuses: PaidArchiveStatus[];
  dateField: PaidArchiveDateField;
  dateFrom?: string;
  dateTo?: string;
  skip?: number;
  limit?: number;
};

export type PaidArchiveDashboardResponse = {
  filters: {
    customer_id: number | null;
    statuses: PaidArchiveStatus[];
    date_field: PaidArchiveDateField;
    date_from: string | null;
    date_to: string | null;
  };
  summary: {
    invoice_count: number;
    amount_by_currency: { currency: string; amount: number }[];
    avg_days_after_due: number | null;
  };
  monthly: {
    month: string;
    currency: string;
    amount: number;
    invoice_count: number;
  }[];
  items: {
    id: number;
    external_id: string;
    customer_organization_id: number | null;
    customer_organization_name?: string | null;
    payment_status: PaidArchiveStatus;
    amount: number;
    currency_type: string;
    date_created_in_accounting_software: string;
    due_date: string;
    date_paid_or_closed: string | null;
    days_after_due: number | null;
  }[];
  total: number;
  skip: number;
  limit: number;
};

function currentMonthRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const from = new Date(Date.UTC(y, m, 1));
  const to = new Date(Date.UTC(y, m + 1, 0));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { dateFrom: iso(from), dateTo: iso(to) };
}

export const defaultPaidArchiveParams = (): PaidArchiveDashboardParams => {
  const { dateFrom, dateTo } = currentMonthRange();
  return {
    statuses: ["Paid", "Closed"],
    dateField: "paid_or_closed",
    dateFrom,
    dateTo,
    skip: 0,
    limit: 50,
  };
};

export const usePaidArchiveDashboardQuery = (
  params: PaidArchiveDashboardParams,
) =>
  useQuery({
    queryKey: paymentKeys.paidArchiveDashboard({ ...params }),
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params.customerId != null) {
        q.set("customer_id", String(params.customerId));
      }
      params.statuses.forEach((status) => q.append("statuses", status));
      q.set("date_field", params.dateField);
      if (params.dateFrom) q.set("date_from", params.dateFrom);
      if (params.dateTo) q.set("date_to", params.dateTo);
      q.set("skip", String(params.skip ?? 0));
      q.set("limit", String(params.limit ?? 50));
      const { data } = await apiClient.get<PaidArchiveDashboardResponse>(
        `/payments/archive/paid-dashboard?${q.toString()}`,
      );
      return data;
    },
  });

export const useAllInvoicesForArchiveQuery = () =>
  useQuery({
    queryKey: paymentKeys.archive(),
    queryFn: async () => {
      const allPayments: ArchivePayment[] = [];
      let skip = 0;
      const limit = 500;
      let hasMore = true;

      while (hasMore) {
        const { data } = await apiClient.get<PaginatedResponse<ArchivePayment>>(
          `/payments/archive?skip=${skip}&limit=${limit}`,
        );
        allPayments.push(...data.items);
        skip += data.items.length;
        hasMore = data.items.length === limit && skip < data.total;
      }

      return allPayments;
    },
  });

export const usePaymentQuery = (
  externalId: string | undefined,
  options?: Pick<UseQueryOptions<Payment>, "enabled" | "staleTime" | "gcTime">,
) =>
  useQuery({
    queryKey: externalId ? paymentKeys.detail(externalId) : [],
    enabled: Boolean(externalId) && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (!externalId) {
        throw new Error("externalId is required");
      }
      const { data } = await apiClient.get<Payment>(`/payments/${externalId}`);
      return data;
    },
  });

export const usePaymentHistoryQuery = (
  externalId: string | undefined,
  options?: Pick<
    UseQueryOptions<PaymentHistory[]>,
    "enabled" | "staleTime" | "gcTime"
  >,
) =>
  useQuery({
    queryKey: externalId ? paymentKeys.history(externalId) : [],
    enabled: Boolean(externalId) && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (!externalId) {
        throw new Error("externalId is required");
      }
      const { data } = await apiClient.get<PaymentHistory[]>(
        `/payments/${externalId}/history`,
      );
      return data;
    },
  });

export const useInvoiceFollowUpsQuery = (externalId: string | undefined) =>
  useQuery({
    queryKey: externalId ? paymentKeys.followUps(externalId) : [],
    enabled: Boolean(externalId),
    queryFn: async () => {
      if (!externalId) throw new Error("externalId is required");
      const { data } = await apiClient.get<InvoiceFollowUp[]>(
        `/payments/${externalId}/follow_up`,
      );
      return data;
    },
  });

type CreateFollowUpVariables = {
  externalId: string;
  follow_up_date: string;
  description?: string | null;
};

export const useCreateInvoiceFollowUpMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      externalId,
      follow_up_date,
      description,
    }: CreateFollowUpVariables) => {
      const { data } = await apiClient.post<
        InvoiceFollowUp,
        AxiosResponse<InvoiceFollowUp>
      >(`/payments/${externalId}/follow_up`, {
        follow_up_date,
        description,
      });
      return data;
    },
    onSuccess: async (_, { externalId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: paymentKeys.followUps(externalId),
        }),
        queryClient.invalidateQueries({
          queryKey: paymentKeys.detail(externalId),
        }),
        queryClient.invalidateQueries({
          queryKey: paymentKeys.open(),
        }),
      ]);
    },
  });
};

type DeleteFollowUpVariables = {
  externalId: string;
  followUpId: number;
};

export const useDeleteInvoiceFollowUpMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ externalId, followUpId }: DeleteFollowUpVariables) => {
      await apiClient.delete(`/payments/${externalId}/follow_up/${followUpId}`);
    },
    onSuccess: async (_, { externalId }) => {
      await queryClient.invalidateQueries({
        queryKey: paymentKeys.followUps(externalId),
      });
    },
  });
};

export const useLastBucketStatusQuery = (
  externalId: string | undefined,
  options?: Pick<
    UseQueryOptions<PaymentHistory | null>,
    "enabled" | "staleTime" | "gcTime"
  >,
) =>
  useQuery({
    queryKey: externalId ? paymentKeys.lastBucketStatus(externalId) : [],
    enabled: Boolean(externalId) && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (!externalId) {
        throw new Error("externalId is required");
      }
      const { data } = await apiClient.get<PaymentHistory | null>(
        `/payments/${externalId}/last-bucket-status`,
      );
      return data;
    },
  });

export const usePaymentCorrespondenceQuery = (
  externalId: string | undefined,
  options?: Pick<
    UseQueryOptions<EmailThread[]>,
    "enabled" | "staleTime" | "gcTime"
  >,
) =>
  useQuery({
    queryKey: externalId
      ? [...paymentKeys.detail(externalId), "correspondence"]
      : [],
    enabled: Boolean(externalId) && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (!externalId) {
        throw new Error("externalId is required");
      }
      const { data } = await apiClient.get<EmailThread[]>(
        `/payments/${externalId}/correspondence`,
      );
      return data;
    },
  });

type MarkThreadEmailsReadVariables = {
  externalId: string;
  gmailThreadId: string;
};

type MarkThreadEmailsReadResponse = {
  thread_id: string;
  updated_count: number;
};

export const useMarkThreadEmailsReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      externalId,
      gmailThreadId,
    }: MarkThreadEmailsReadVariables) => {
      const { data } = await apiClient.post<MarkThreadEmailsReadResponse>(
        `/payments/${externalId}/threads/${encodeURIComponent(gmailThreadId)}/mark_read`,
      );
      return data;
    },
    onSuccess: async (_, { externalId }) => {
      await queryClient.invalidateQueries({
        queryKey: [...paymentKeys.detail(externalId), "correspondence"],
      });
    },
  });
};

export const usePaymentMessageDraftQuery = (
  externalId: string | undefined,
  options?: Pick<
    UseQueryOptions<EmailDraft | null>,
    "enabled" | "staleTime" | "gcTime"
  >,
) =>
  useQuery({
    queryKey: externalId
      ? [...paymentKeys.detail(externalId), "message_draft"]
      : [],
    enabled: Boolean(externalId) && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (!externalId) {
        throw new Error("externalId is required");
      }
      const { data } = await apiClient.get<EmailDraft | null>(
        `/email/drafts/${externalId}`,
      );
      return data;
    },
  });

type SaveEmailDraftVariables = {
  paymentExternalId: string;
  draftData: {
    text: string;
    to: string[];
    subject: string;
    cc?: string[] | null;
    bcc?: string[] | null;
    thread_id?: string | null;
    reply_message_id?: string | null;
  };
};

export const useSaveEmailDraftMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paymentExternalId,
      draftData,
    }: SaveEmailDraftVariables) => {
      const { data } = await apiClient.put<EmailDraft>(
        `/email/drafts/${paymentExternalId}`,
        draftData,
      );
      return data;
    },
    onSuccess: async (_, { paymentExternalId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: paymentKeys.emailDrafts() }),
        queryClient.invalidateQueries({
          queryKey: [...paymentKeys.detail(paymentExternalId), "message_draft"],
        }),
      ]);
    },
  });
};

type LatestThreadSubjectResponse = {
  subject: string | null;
};

export type ThreadOption = {
  thread_id: string;
  subject: string;
};

type ComposerContextResponse = {
  thread_options: ThreadOption[];
  draft_thread_subject: string | null;
};

export const usePaymentComposerContextQuery = (
  externalId: string | undefined,
  options?: Pick<
    UseQueryOptions<ComposerContextResponse>,
    "enabled" | "staleTime" | "gcTime"
  >,
) =>
  useQuery({
    queryKey: externalId
      ? [...paymentKeys.detail(externalId), "composer_context"]
      : [],
    enabled: Boolean(externalId) && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (!externalId) {
        throw new Error("externalId is required");
      }
      const { data } = await apiClient.get<ComposerContextResponse>(
        `/payments/${externalId}/composer_context`,
      );
      return data;
    },
  });

export const usePaymentLatestThreadSubjectQuery = (
  externalId: string | undefined,
  options?: Pick<
    UseQueryOptions<LatestThreadSubjectResponse>,
    "enabled" | "staleTime" | "gcTime"
  >,
) =>
  useQuery({
    queryKey: externalId
      ? [...paymentKeys.detail(externalId), "latest_thread_subject"]
      : [],
    enabled: Boolean(externalId) && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (!externalId) {
        throw new Error("externalId is required");
      }
      const { data } = await apiClient.get<LatestThreadSubjectResponse>(
        `/payments/${externalId}/latest_thread_subject`,
      );
      return data;
    },
  });

type InvoicePdfBatchResponse = {
  results: Array<{
    external_id: string;
    status: string;
    pdf_base64?: string;
    error?: string;
  }>;
};

export const useInvoicesPdfMutation = () => {
  return useMutation({
    mutationKey: paymentKeys.invoicesPdf(),
    mutationFn: async (invoiceIds: string[]) => {
      const { data } = await apiClient.post<InvoicePdfBatchResponse>(
        "/payments/invoices/pdf",
        {
          invoice_ids: invoiceIds,
        },
      );
      return data;
    },
    onError: async (error: any) => {
      const detail = error?.response?.data?.detail;
      const code =
        typeof detail === "object" && detail !== null && "code" in detail
          ? (detail as { code?: string }).code
          : undefined;
      const message =
        typeof detail === "object" && detail !== null && "message" in detail
          ? (detail as { message?: string }).message
          : undefined;

      if (
        error?.response?.status === 400 &&
        code === "NO_ACCOUNTING_SOFTWARE"
      ) {
        toast.error(
          message ??
            "No accounting software connected. Reconnect under Files & Integrations.",
        );
        return;
      }

      if (error?.response?.status === 403) {
        toast.error(message ?? INVOICE_PDF_ACCOUNTING_RECONNECT_MESSAGE);
      }
    },
  });
};

let warmupInvoicesPdfPromise: Promise<unknown> | null = null;

export const warmupInvoicesPdf = () => {
  if (!warmupInvoicesPdfPromise) {
    warmupInvoicesPdfPromise = apiClient
      .post<{
        status: string;
        reason?: string;
      }>("/payments/invoices/pdf/warmup")
      .finally(() => {
        warmupInvoicesPdfPromise = null;
      });
  }
  return warmupInvoicesPdfPromise;
};

type SendEmailVariables = {
  emailData: {
    text: string;
    subject: string;
    to: string[];
    cc?: string[] | null;
    bcc?: string[] | null;
    threadId?: string | null;
    replyMessageId?: string | null;
  };
  draftUrl: string | null;
  attachments: File[];
  paymentExternalId?: string | null;
  paymentExternalIds?: string[];
  invalidateReceivables?: boolean;
  updateIntentionStatus?: boolean;
};

type SendEmailResponse = {
  status: string;
  message_id?: string;
  reason?: string;
  message?: string;
};

const buildEmailFormData = ({
  emailData,
  draftUrl,
  attachments,
  paymentExternalId,
  paymentExternalIds,
  updateIntentionStatus = true,
}: SendEmailVariables) => {
  const formData = new FormData();
  formData.append("text", emailData.text);
  formData.append("subject", emailData.subject);
  // Always send arrays as JSON strings
  formData.append("to", JSON.stringify(emailData.to));
  formData.append(
    "cc",
    emailData.cc && emailData.cc.length > 0 ? JSON.stringify(emailData.cc) : "",
  );
  formData.append(
    "bcc",
    emailData.bcc && emailData.bcc.length > 0
      ? JSON.stringify(emailData.bcc)
      : "",
  );
  formData.append("thread_id", emailData.threadId ?? "");
  formData.append("reply_message_id", emailData.replyMessageId ?? "");

  if (draftUrl) {
    formData.append("message_draft_url", draftUrl);
  }

  if (paymentExternalIds && paymentExternalIds.length > 0) {
    formData.append("payment_external_ids", JSON.stringify(paymentExternalIds));
  } else if (paymentExternalId) {
    formData.append(
      "payment_external_ids",
      JSON.stringify([paymentExternalId]),
    );
  }

  const files = attachments.length
    ? attachments
    : [new File([""], "empty.txt", { type: "text/plain" })];

  files.forEach((file) => formData.append("attachments", file));

  formData.append(
    "update_intention_status",
    updateIntentionStatus ? "true" : "false",
  );

  return formData;
};

export const useSendEmailMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: SendEmailVariables) => {
      const formData = buildEmailFormData(variables);
      const { data } = await apiClient.post<SendEmailResponse>(
        "/payments/send_email",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return data;
    },
    onSuccess: async (_, variables) => {
      const invalidatePromises = [
        queryClient.invalidateQueries({ queryKey: paymentKeys.open() }),
        queryClient.invalidateQueries({ queryKey: paymentKeys.emailDrafts() }),
      ];

      if (variables.paymentExternalId) {
        invalidatePromises.push(
          queryClient.invalidateQueries({
            queryKey: paymentKeys.detail(variables.paymentExternalId),
          }),
          queryClient.invalidateQueries({
            queryKey: paymentKeys.history(variables.paymentExternalId),
          }),
        );
      }

      if (variables.invalidateReceivables) {
        invalidatePromises.push(
          queryClient.invalidateQueries({ queryKey: receivablesKeys.all }),
        );
      }

      await Promise.all(invalidatePromises);
    },
  });
};

type RegenerateEmailVariables = {
  emailText: string;
  userPrompt: string;
  paymentExternalId?: string;
  paymentExternalIds?: string[];
  threadId?: string;
  signatureEnabled?: boolean;
};

type RegenerateEmailResponse = {
  text: string;
};

export const useRegenerateEmailMutation = () =>
  useMutation({
    mutationFn: async ({
      emailText,
      userPrompt,
      paymentExternalId,
      paymentExternalIds,
      threadId,
      signatureEnabled,
    }: RegenerateEmailVariables) => {
      const { data } = await apiClient.post<RegenerateEmailResponse>(
        "/payments/regenerate_email",
        {
          email_text: emailText,
          user_prompt: userPrompt,
          payment_external_id: paymentExternalId,
          payment_external_ids: paymentExternalIds,
          thread_id: threadId,
          signature_enabled: signatureEnabled,
        },
      );
      return data;
    },
  });

type UpdatePaymentIntentionVariables = {
  externalId: string;
  intentionStatus: IntentionStatus;
  description?: string;
};

type UpdatePaymentIntentionResponse = {
  status: string;
  intention_status: IntentionStatus;
};

type UpdatePaymentEmailSendingVariables = {
  externalId: string;
  isEmailSendingEnabled: boolean;
};

export const useUpdatePaymentEmailSendingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      externalId,
      isEmailSendingEnabled,
    }: UpdatePaymentEmailSendingVariables) => {
      const { data } = await apiClient.patch<Payment>(
        `/payments/${externalId}/email_sending`,
        { is_email_sending_enabled: isEmailSendingEnabled },
      );
      return data;
    },
    onSuccess: async (_, { externalId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: paymentKeys.open() }),
        queryClient.invalidateQueries({
          queryKey: paymentKeys.detail(externalId),
        }),
        queryClient.invalidateQueries({ queryKey: receivablesKeys.all }),
      ]);
    },
  });
};

export const useUpdatePaymentIntentionStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      externalId,
      intentionStatus,
      description,
    }: UpdatePaymentIntentionVariables) => {
      const url = `/payments/${externalId}/intention_status?intention_status=${intentionStatus}`;
      const { data } = await apiClient.patch<UpdatePaymentIntentionResponse>(
        url,
        description ? { description } : {},
      );
      return data;
    },
    onSuccess: async (_, { externalId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: paymentKeys.open() }),
        queryClient.invalidateQueries({
          queryKey: paymentKeys.detail(externalId),
        }),
        queryClient.invalidateQueries({
          queryKey: paymentKeys.history(externalId),
        }),
        queryClient.invalidateQueries({
          queryKey: [...paymentKeys.all, "unified-bucket-status"],
        }),
        queryClient.invalidateQueries({
          queryKey: [...customerRepresentativeKeys.all, "enriched"],
        }),
      ]);
    },
  });
};

type UnifiedBucketStatusValues = {
  customer_count: number;
  invoice_count: number;
  amount: number;
};

type UnifiedBucketStatus = {
  Approved: UnifiedBucketStatusValues;
  Unresolved: UnifiedBucketStatusValues;
  No_reply: UnifiedBucketStatusValues;
  Needs_attention: UnifiedBucketStatusValues;
  In_progress: UnifiedBucketStatusValues;
};

export const useUnifiedBucketStatusQuery = () =>
  useQuery({
    queryKey: [...paymentKeys.all, "unified-bucket-status"],
    queryFn: async () => {
      const { data } = await apiClient.get<UnifiedBucketStatus>(
        `/payments/unified-bucket-status`,
      );
      return data;
    },
  });

type GenerateDraftsVariables = {
  template_id?: number;
} & (
  | { payment_external_ids: string[] }
  | { customer_organization_ids: number[] }
);

type GenerateDraftsResponse = {
  generated_count: number;
  skipped_count: number;
  failed_count: number;
};

type BulkDraftJobStatus = {
  job_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  generated_count: number;
  skipped_count: number;
  failed_count: number;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
};

export const useGenerateDraftsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: GenerateDraftsVariables) => {
      const { data: jobResponse } = await apiClient.post<{
        job_id: string;
        status: string;
        message: string;
      }>("/payments/generate_drafts", variables);

      const jobId = jobResponse.job_id;
      const pollInterval = 1000;
      const maxPollTime = 300000;
      const startTime = Date.now();

      while (Date.now() - startTime < maxPollTime) {
        const { data: status } = await apiClient.get<BulkDraftJobStatus>(
          `/payments/bulk_draft_job/${jobId}/status`,
        );

        if (status.status === "completed" || status.status === "failed") {
          if (status.status === "failed" && status.error_message) {
            throw new Error(status.error_message);
          }
          return {
            generated_count: status.generated_count,
            skipped_count: status.skipped_count,
            failed_count: status.failed_count,
          } satisfies GenerateDraftsResponse;
        }

        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }

      throw new Error(
        "Draft generation timed out. It may still be processing — refresh in a moment.",
      );
    },
    onSuccess: async (_data, variables) => {
      const invalidations: Promise<void>[] = [
        queryClient.invalidateQueries({
          queryKey: paymentKeys.open(),
        }),
        queryClient.invalidateQueries({
          queryKey: receivablesKeys.all,
        }),
      ];

      if ("payment_external_ids" in variables) {
        for (const externalId of variables.payment_external_ids) {
          invalidations.push(
            queryClient.invalidateQueries({
              queryKey: [...paymentKeys.detail(externalId), "message_draft"],
            }),
          );
        }
      }

      await Promise.all(invalidations);
    },
  });
};

type GetEmailAddressesVariables =
  | { payment_external_ids: string[] }
  | { customer_organization_ids: number[] };

type GetEmailAddressesResponse = {
  email_addresses: string[];
  invoices_with_drafts: string[];
  invoices_without_drafts: string[];
};

function hasEmailAddressScope(
  variables: GetEmailAddressesVariables | null,
): variables is GetEmailAddressesVariables {
  if (!variables) return false;
  const hasInvoiceScope =
    "payment_external_ids" in variables &&
    variables.payment_external_ids.length > 0;
  const hasCustomerScope =
    "customer_organization_ids" in variables &&
    variables.customer_organization_ids.length > 0;
  return hasInvoiceScope !== hasCustomerScope;
}

export const useGetEmailAddressesQuery = (
  variables: GetEmailAddressesVariables | null,
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: ["payments", "email-addresses", variables],
    enabled: enabled && hasEmailAddressScope(variables),
    queryFn: async () => {
      const { data } = await apiClient.post<GetEmailAddressesResponse>(
        "/payments/get_email_addresses",
        variables!,
      );
      return data;
    },
  });

type BulkSendEmailsVariables = {
  payment_external_ids: string[];
  includeInvoicePdfs?: boolean;
};

type BulkEmailJobStatus = {
  job_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  total_count: number;
  successful_count: number;
  failed_count: number;
  skipped_count: number;
  successful_ids: string[];
  failed_ids: string[];
  skipped_ids: string[];
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
};

export const useBulkSendEmailsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      payment_external_ids,
      includeInvoicePdfs = false,
    }: BulkSendEmailsVariables) => {
      // Queue the bulk email job
      const { data: jobResponse } = await apiClient.post<{
        job_id: string;
        status: string;
        message: string;
      }>("/payments/bulk_send_emails", {
        payment_external_ids,
        include_invoice_pdfs: includeInvoicePdfs,
      });

      const jobId = jobResponse.job_id;

      // Poll for job status
      const pollInterval = 1000; // Poll every 1 second
      const maxPollTime = 300000; // Max 5 minutes
      const startTime = Date.now();

      while (Date.now() - startTime < maxPollTime) {
        const { data: status } = await apiClient.get<BulkEmailJobStatus>(
          `/payments/bulk_email_job/${jobId}/status`,
        );

        if (status.status === "completed" || status.status === "failed") {
          return {
            successful: status.successful_ids,
            failed: status.failed_ids.map((id) => ({
              external_id: id,
              error: status.error_message || "Unknown error",
            })),
            skipped: status.skipped_ids,
          };
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }

      // Timeout - try to get final status
      try {
        const { data: status } = await apiClient.get<BulkEmailJobStatus>(
          `/payments/bulk_email_job/${jobId}/status`,
        );
        return {
          successful: status.successful_ids,
          failed: status.failed_ids.map((id) => ({
            external_id: id,
            error: status.error_message || "Unknown error",
          })),
          skipped: status.skipped_ids,
        };
      } catch (error) {
        throw new Error("Job polling timed out");
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: paymentKeys.open() }),
        queryClient.invalidateQueries({ queryKey: paymentKeys.emailDrafts() }),
      ]);
    },
  });
};

async function invalidatePaymentNotesQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  paymentExternalId: string,
) {
  await queryClient.invalidateQueries({
    queryKey: paymentKeys.notes(paymentExternalId),
  });
}

export const usePaymentNotesHistoryQuery = (
  paymentExternalId: string | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey:
      paymentExternalId != null && paymentExternalId !== ""
        ? paymentKeys.notes(paymentExternalId)
        : [],
    enabled: Boolean(paymentExternalId) && (options?.enabled ?? true),
    queryFn: async () => {
      if (!paymentExternalId) throw new Error("paymentExternalId is required");
      const { data } = await apiClient.get<CustomerNoteHistory[]>(
        `/payments/${encodeURIComponent(paymentExternalId)}/notes`,
      );
      return data;
    },
  });

export const useAddPaymentNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      paymentExternalId,
      body,
      files = [],
    }: {
      paymentExternalId: string;
      body?: string;
      files?: File[];
    }) => {
      const trimmedBody = body?.trim() ?? "";
      if (!trimmedBody && files.length === 0) {
        throw new Error("Note must include text or at least one attachment.");
      }

      const formData = new FormData();
      if (trimmedBody) {
        formData.append("body", trimmedBody);
      }
      for (const file of files) {
        formData.append("files", file);
      }

      const { data } = await apiClient.post<CustomerNoteHistory>(
        `/payments/${encodeURIComponent(paymentExternalId)}/notes`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return data;
    },
    onSuccess: async (_, { paymentExternalId }) => {
      await invalidatePaymentNotesQueries(queryClient, paymentExternalId);
    },
  });
};

export const useUpdatePaymentNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      paymentExternalId,
      noteId,
      body,
    }: {
      paymentExternalId: string;
      noteId: number;
      body: string;
    }) => {
      const { data } = await apiClient.patch<CustomerNoteHistory>(
        `/payments/${encodeURIComponent(paymentExternalId)}/notes/${noteId}`,
        { body },
      );
      return data;
    },
    onSuccess: async (_, { paymentExternalId }) => {
      await invalidatePaymentNotesQueries(queryClient, paymentExternalId);
    },
  });
};

export const useDeletePaymentNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      paymentExternalId,
      noteId,
    }: {
      paymentExternalId: string;
      noteId: number;
    }) => {
      await apiClient.delete(
        `/payments/${encodeURIComponent(paymentExternalId)}/notes/${noteId}`,
      );
    },
    onSuccess: async (_, { paymentExternalId }) => {
      await invalidatePaymentNotesQueries(queryClient, paymentExternalId);
    },
  });
};
